// ─── Configuration du calendrier — accordéons ───────────────────
// Accessible via appui long sur l'onglet Calendrier. Organisée en sections
// repliables pour tenir sur un téléphone :
//   • Type & gardes : co-parentalité → active les fonctions de garde.
//   • Affichage : grille horaire des vues Semaine / Jour.
//   • Accès des membres : qui voit / écrit / configure ce calendrier.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, SectionPliable, SelecteurHeure, SelecteurCouleur, Bascule, ChoixPuces, useEspace, membresDe, db } from '@mop/core';
import { doc, getDoc } from 'firebase/firestore';
import { CONFIG_DEFAUT, normaliserConfig, chargerAffichageConfig, sauvegarderAffichageConfig } from './services/affichageConfig';
import { TYPES_CALENDRIER, definirTypeCalendrier } from './services/calendriersEspace';
import { lireParents, definirParents, PALETTE_GARDE, GARDE_COL_DEFAUT } from './services/garde';
import { useCalendrierActif } from './CalendrierActifContext';

const hLabel = (h) => `${h}h`;

export default function FeuilleConfigCalendrier({ visible, calId, onFermer, onApplique }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { espaceActif } = useEspace();
  const { calendriers } = useCalendrierActif();
  const calObj = calendriers.find(c => c.id === calId) || null;

  // Membres = ceux de l'espace PROPRIÉTAIRE du calendrier (pas l'espace actif du
  // lecteur) : sur un calendrier PARTAGÉ, l'espace actif n'est pas le bon. Si le
  // calendrier appartient à l'espace actif, on l'utilise directement (déjà à jour).
  const [espaceCal, setEspaceCal] = useState(null);
  useEffect(() => {
    const eid = calObj?.espaceId;
    if (!visible || !eid || (espaceActif && espaceActif.id === eid)) { setEspaceCal(null); return; }
    getDoc(doc(db, 'espaces', eid))
      .then(sn => setEspaceCal(sn.exists() ? { id: sn.id, ...sn.data() } : null))
      .catch(() => setEspaceCal(null));
  }, [visible, calObj?.espaceId, espaceActif?.id]);
  const espaceMembres = espaceCal || espaceActif;

  const ownerUid = calObj?.creePar || espaceMembres?.creePar || null;
  const membres = useMemo(() => membresDe(espaceMembres), [espaceMembres]);
  const invites = espaceMembres?.invitations || [];   // invités en attente de 1re connexion

  const [cfg, setCfg] = useState(CONFIG_DEFAUT);
  const [type, setType] = useState('general');
  const [par, setPar] = useState({ nom1: '', p1Uid: null, p1Email: null, nom2: '', p2Uid: null, p2Email: null, couleur1: GARDE_COL_DEFAUT.parent1, couleur2: GARDE_COL_DEFAUT.parent2, demandes: true });
  const [ouvert, setOuvert] = useState('type');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [pickHeure, setPickHeure] = useState(null);   // 'heureDebut' | 'heureFin' | null (modale d'heure)
  // Entier d'heure ↔ "HH:MM" pour la modale d'heure (24 = minuit fin de journée).
  const hToHHMM = (h) => `${String(h >= 24 ? 0 : h).padStart(2, '0')}:00`;
  const hhmmToH = (hhmm, estFin) => { let h = parseInt(String(hhmm || '').slice(0, 2), 10); if (Number.isNaN(h)) return null; if (estFin && h === 0) h = 24; return h; };

  // Instantané initial pour griser la disquette tant que rien n'a changé (app-wide).
  const init = useRef({ cfg: '', type: '', par: '' });
  useEffect(() => {
    if (!visible || !calId) return;
    chargerAffichageConfig(calId).then(c => { setCfg(c); init.current.cfg = JSON.stringify(normaliserConfig(c)); }).catch(() => {});
    // Le type LEGACY « enfant » (calendriers MoPCal) = co-parentalité.
    const t0 = calObj?.type === 'enfant' ? 'coparentalite' : (calObj?.type || 'general');
    setType(t0); init.current.type = t0;
    lireParents(calId).then(p => {
      const pv = {
        nom1: p.parent1?.nom && p.parent1.nom !== 'Parent 1' ? p.parent1.nom : '',
        p1Uid: p.parent1?.uid || null, p1Email: p.parent1?.email || null,
        nom2: p.parent2?.nom && p.parent2.nom !== 'Parent 2' ? p.parent2.nom : '',
        p2Uid: p.parent2?.uid || null, p2Email: p.parent2?.email || null,
        couleur1: p.couleur1 || GARDE_COL_DEFAUT.parent1,
        couleur2: p.couleur2 || GARDE_COL_DEFAUT.parent2,
        demandes: p.demandesActives !== false,
      };
      setPar(pv); init.current.par = JSON.stringify(pv);
    }).catch(() => {});
    setMsg('');
  }, [visible, calId, calObj?.type]);

  const modifie = JSON.stringify(normaliserConfig(cfg)) !== init.current.cfg
    || type !== init.current.type
    || JSON.stringify(par) !== init.current.par;

  // Personnes assignables comme parent : membres (uid) + invités (e-mail).
  const gens = [
    ...membres.map(m => ({ cle: `u:${m.uid}`, uid: m.uid, email: null, label: m.nom || m.uid.slice(0, 6) })),
    ...invites.map(mail => ({ cle: `e:${mail}`, uid: null, email: mail, label: mail })),
  ];
  const choisirParent = (n, g) => setPar(p => {
    const nomCle = n === 1 ? 'nom1' : 'nom2';
    const dej = n === 1 ? p.nom1 : p.nom2;
    return {
      ...p,
      [n === 1 ? 'p1Uid' : 'p2Uid']: g?.uid || null,
      [n === 1 ? 'p1Email' : 'p2Email']: g?.email || null,
      [nomCle]: dej || (g ? (g.uid ? g.label : g.email) : dej),
    };
  });
  const parentSel = (n) => (n === 1 ? (par.p1Uid ? `u:${par.p1Uid}` : par.p1Email ? `e:${par.p1Email}` : null)
                                    : (par.p2Uid ? `u:${par.p2Uid}` : par.p2Email ? `e:${par.p2Email}` : null));

  const maj = (champs) => { setCfg(c => normaliserConfig({ ...c, ...champs })); setMsg(''); };

  async function enregistrer() {
    if (!calId || busy) return;
    setBusy(true);
    try {
      const norm = normaliserConfig(cfg);
      await sauvegarderAffichageConfig(calId, norm);
      if (type !== (calObj?.type || 'general')) await definirTypeCalendrier(calId, type);
      if (type === 'coparentalite') await definirParents(calId, {
        nomParent1: par.nom1, parent1Uid: par.p1Uid, emailParent1: par.p1Email,
        nomParent2: par.nom2, parent2Uid: par.p2Uid, emailParent2: par.p2Email,
        couleurParent1: par.couleur1, couleurParent2: par.couleur2,
        demandesActives: par.demandes,
      });
      onApplique?.(norm);
      setMsg(t('calendrier.configEnregistree'));
    } catch (e) { setMsg(e?.message || t('messagerie.enregistrerImpossible')); }
    setBusy(false);
  }

  const heuresDebut = [0, 5, 6, 7, 8, 9];
  const heuresFin   = [18, 20, 21, 22, 23, 24];
  const zooms = [{ v: 40, l: t('calendrier.zoomCompact') }, { v: 56, l: t('calendrier.zoomNormal') }, { v: 80, l: t('calendrier.zoomLarge') }];
  const durees = [30, 45, 60, 90, 120];
  const vues = [['mois', t('calendrier.vueMois')], ['semaine', t('calendrier.vueSemaine')], ['jour', t('calendrier.vueJour')]];
  const bascule = (cle) => setOuvert(o => (o === cle ? null : cle));
  const estCoparent = type === 'coparentalite';

  return (
    <>
    <Feuille visible={visible} titre={t('calendrier.configTitre')} emoji="⚙️" onFermer={onFermer}
      action={{ icone: '💾', onPress: enregistrer, disabled: busy || !modifie, label: t('commun.enregistrer') }}>

      {/* ── Type & gardes ── */}
      <SectionPliable titre={t('calendrier.sectionType')} emoji="👶"
        sousTitre={estCoparent ? t('calendrier.sousTitreCoparent') : t('calendrier.sousTitreGeneral')}
        ouvert={ouvert === 'type'} onToggle={() => bascule('type')}>
        <Text style={s.intro}>{t('calendrier.introType')}</Text>
        <ChoixPuces value={type} onChange={(id) => { setType(id); setMsg(''); }}
          options={TYPES_CALENDRIER.map(ty => ({ id: ty.id, label: `${ty.emoji} ${ty.label}` }))} />
        {!estCoparent && <Text style={s.note}>{t('calendrier.noteGardesGrisees')}</Text>}

        {estCoparent && (
          <View style={s.parents}>
            {[1, 2].map(n => {
              const nom = n === 1 ? par.nom1 : par.nom2;
              const sel = parentSel(n);
              return (
                <View key={n} style={s.parentBloc}>
                  <Text style={s.parentTitre}>{n === 1 ? t('garde.parent1') : t('garde.parent2')}</Text>
                  <TextInput style={s.input} value={nom} onChangeText={(v) => setPar(p => ({ ...p, [n === 1 ? 'nom1' : 'nom2']: v }))}
                    placeholder={t('calendrier.phNomParent', { n })} placeholderTextColor={UI.faint} />
                  <Text style={s.parentLbl}>{t('calendrier.personneAssociee')}</Text>
                  <View style={s.puces}>
                    <TouchableOpacity style={[s.puce, !sel && s.puceOn]} onPress={() => choisirParent(n, null)}>
                      <Text style={[s.puceTxt, !sel && s.puceTxtOn]}>{t('calendrier.aucune')}</Text>
                    </TouchableOpacity>
                    {gens.map(g => (
                      <TouchableOpacity key={g.cle} style={[s.puce, sel === g.cle && s.puceOn]} onPress={() => choisirParent(n, g)}>
                        <Text style={[s.puceTxt, sel === g.cle && s.puceTxtOn]} numberOfLines={1}>{g.email ? '✉️ ' : '👤 '}{g.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <Text style={s.parentLbl}>{t('profil.couleur')}</Text>
                  <SelecteurCouleur valeur={n === 1 ? par.couleur1 : par.couleur2} palette={PALETTE_GARDE}
                    onChange={(c) => setPar(p => ({ ...p, [n === 1 ? 'couleur1' : 'couleur2']: c }))} />
                </View>
              );
            })}
            <View style={s.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.switchTitre}>{t('calendrier.demandesChangement')}</Text>
                <Text style={s.switchSous}>{t('calendrier.demandesChangementSous')}</Text>
              </View>
              <Bascule valeur={par.demandes} onChange={(v) => setPar(p => ({ ...p, demandes: v }))} accessibilityLabel={t('calendrier.demandesChangementA11y')} />
            </View>
            {gens.length === 0 && <Text style={s.note}>{t('calendrier.noteInviterMembres')}</Text>}
          </View>
        )}
      </SectionPliable>

      {/* ── Affichage ── */}
      <SectionPliable titre={t('budget.affichage')} emoji="🕑" sousTitre={`${cfg.heureDebut}h–${cfg.heureFin}h · ${vues.find(v => v[0] === cfg.vueDefaut)?.[1]}`}
        ouvert={ouvert === 'affichage'} onToggle={() => bascule('affichage')}>
        <Text style={s.intro}>{t('calendrier.introAffichage')}</Text>

        <View style={s.heuresRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('calendrier.heureDebut')}</Text>
            <TouchableOpacity style={s.champHeure} onPress={() => setPickHeure('heureDebut')}>
              <Text style={s.champHeureTxt}>🕒 {hLabel(cfg.heureDebut)}</Text>
            </TouchableOpacity>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('calendrier.heureFin')}</Text>
            <TouchableOpacity style={s.champHeure} onPress={() => setPickHeure('heureFin')}>
              <Text style={s.champHeureTxt}>🕒 {hLabel(cfg.heureFin)}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <Text style={s.lbl}>{t('calendrier.hauteurHeure')}</Text>
        <View style={s.puces}>{zooms.map(z => (
          <TouchableOpacity key={z.v} style={[s.puce, cfg.hauteurHeure === z.v && s.puceOn]} onPress={() => maj({ hauteurHeure: z.v })}>
            <Text style={[s.puceTxt, cfg.hauteurHeure === z.v && s.puceTxtOn]}>{z.l}</Text>
          </TouchableOpacity>))}</View>

        {/* Pas des créneaux d'affichage (Semaine/Jour) — le pincement règle
            ensuite la hauteur d'UN créneau, comme sur l'EDT. */}
        <Text style={s.lbl}>{t('calendrier.pasAffichage')}</Text>
        <View style={s.puces}>{[[15, '15 min'], [30, '30 min'], [45, '45 min'], [60, '1 h'], [120, '2 h']].map(([v, lab]) => (
          <TouchableOpacity key={v} style={[s.puce, (cfg.pasMinutes || 60) === v && s.puceOn]} onPress={() => maj({ pasMinutes: v })}>
            <Text style={[s.puceTxt, (cfg.pasMinutes || 60) === v && s.puceTxtOn]}>{lab}</Text>
          </TouchableOpacity>))}</View>

        <Text style={s.lbl}>{t('calendrier.dureeDefaut')}</Text>
        <View style={s.puces}>{durees.map(d => (
          <TouchableOpacity key={d} style={[s.puce, cfg.dureeDefaut === d && s.puceOn]} onPress={() => maj({ dureeDefaut: d })}>
            <Text style={[s.puceTxt, cfg.dureeDefaut === d && s.puceTxtOn]}>{d} min</Text>
          </TouchableOpacity>))}</View>

        <Text style={s.lbl}>{t('calendrier.vueDemarrage')}</Text>
        <View style={s.puces}>{vues.map(([v, lab]) => (
          <TouchableOpacity key={v} style={[s.puce, cfg.vueDefaut === v && s.puceOn]} onPress={() => maj({ vueDefaut: v })}>
            <Text style={[s.puceTxt, cfg.vueDefaut === v && s.puceTxtOn]}>{lab}</Text>
          </TouchableOpacity>))}</View>

        {/* Événements hors plage : étendre la grille pour les montrer, ou plage stricte. */}
        <View style={s.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchTitre}>{t('calendrier.horsPlageTitre')}</Text>
            <Text style={s.switchSous}>{t('calendrier.horsPlageSous')}</Text>
          </View>
          <Bascule valeur={cfg.horsPlage !== false} onChange={(v) => maj({ horsPlage: v })} accessibilityLabel={t('calendrier.horsPlageTitre')} />
        </View>
      </SectionPliable>

      {/* ── Accès des membres : déplacé dans Profil (accès unifié) ── */}
      <Text style={s.note}>🔐 {t('calendrier.accesDansProfil')}</Text>

      {!!msg && <Text style={s.msg}>{msg}</Text>}
    </Feuille>

    {/* Modale d'heure (calque) — comme la création d'un événement. */}
    <SelecteurHeure visible={!!pickHeure}
      titre={pickHeure === 'heureFin' ? t('calendrier.heureFin') : t('calendrier.heureDebut')}
      valeur={pickHeure ? hToHHMM(cfg[pickHeure]) : ''}
      min={pickHeure === 'heureFin' ? hToHHMM(cfg.heureDebut + 1) : undefined}
      onChange={(hhmm) => { const h = hhmmToH(hhmm, pickHeure === 'heureFin'); if (h != null) maj({ [pickHeure]: h }); }}
      onFermer={() => setPickHeure(null)} />
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  intro: { fontSize: 12.5, color: UI.muted, lineHeight: 18, marginBottom: 8 },
  lbl:   { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  puces: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  heuresRow: { flexDirection: 'row', gap: 12 },
  champHeure: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 0.5, borderColor: UI.champBord },
  champHeureTxt: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  puce:  { paddingHorizontal: 13, paddingVertical: 8, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  puceOn:{ backgroundColor: UI.accentTint, borderColor: UI.accent },
  puceTxt:{ fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  puceTxtOn:{ color: UI.accent, fontWeight: '800' },
  note:  { fontSize: 11.5, color: UI.faint, marginTop: 10, lineHeight: 16 },
  parents: { marginTop: 14, borderTopWidth: 0.5, borderTopColor: UI.border, paddingTop: 10 },
  parentBloc: { marginBottom: 12 },
  parentTitre: { fontSize: 13, fontWeight: '800', color: UI.ink, marginBottom: 6 },
  parentLbl: { fontSize: 11.5, fontWeight: '700', color: UI.muted, marginTop: 8, marginBottom: 6 },
  pastilleCol: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: 'transparent' },
  pastilleColOn: { borderColor: UI.ink },
  pastilleColCheck: { color: '#fff', fontSize: 15, fontWeight: '900' },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 6, paddingTop: 10, borderTopWidth: 0.5, borderTopColor: UI.border },
  switchTitre: { fontSize: 13, fontWeight: '700', color: UI.ink },
  switchSous: { fontSize: 11.5, color: UI.muted, marginTop: 2, lineHeight: 16 },
  pointer: { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 8, backgroundColor: UI.champ, borderRadius: 10, padding: 10, borderWidth: 0.5, borderColor: UI.champBord },
  pointerFort: { fontWeight: '800', color: UI.ink },
  vide:  { fontSize: 12.5, color: UI.faint, fontStyle: 'italic' },
  msg:   { fontSize: 12.5, color: UI.accent, marginTop: 12, textAlign: 'center' },
  accEntete: { flexDirection: 'row', alignItems: 'flex-end', marginTop: 4, marginBottom: 4 },
  accNomEntete: {},
  accColLbl: { width: 46, textAlign: 'center', fontSize: 10, fontWeight: '700', color: UI.faint, lineHeight: 13 },
  accLigne: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderTopWidth: 0.5, borderTopColor: UI.border },
  accNom: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  accSous: { fontSize: 11, color: UI.muted, marginTop: 1 },
  accCase: { width: 46, alignItems: 'center', justifyContent: 'center' },
  accCaseTxt: { fontSize: 18, color: UI.faint, fontWeight: '700' },
  accCaseOn: {},
  accCaseTxtOn: { color: UI.accent },
});
